import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
// import { jwtDecode } from 'jwt-decode';

const api = axios.create({
  baseURL: '/api',
});

// Utility to check if a JWT is expired
// const isTokenExpired = (token: string) => {
//   try {
//     const decoded = jwtDecode<{ exp: number }>(token);
//     return decoded.exp * 1000 < Date.now() + 10000; // expire in 10s
//   } catch {
//     return true;
//   }
// };

// --- 🔄 Pending Request Deduplication ---
const pendingRequests = new Map<string, Promise<AxiosResponse>>();
const cache = new Map<string, AxiosResponse>();

function getRequestKey(config: AxiosRequestConfig): string {
  const { method, url, params, data } = config;
  return `${method?.toUpperCase()}:${url}?${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
}

async function retry<T = any>(fn: () => Promise<T>, retries = 2): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (retries <= 0) throw err;
    console.warn("Retrying request...");
    return retry(fn, retries - 1);
  }
}

export async function dedupedRequest<T = any>(
    config: AxiosRequestConfig,
    { useCache = true, maxRetries = 2 }: { useCache?: boolean; maxRetries?: number } = {}
  ): Promise<AxiosResponse<T>> {
  const key = getRequestKey(config);

  if (config.method?.toUpperCase() === 'GET' && useCache && cache.has(key)) {
    return cache.get(key)! as AxiosResponse<T>;
  }

  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)! as Promise<AxiosResponse<T>>;
  }

  const request = retry(() => api(config), maxRetries)
    .then((response) => {
      pendingRequests.delete(key);
      if (config.method?.toUpperCase() === 'GET' && useCache) {
        cache.set(key, response);
      }
      return response;
    })
    .catch((error) => {
      pendingRequests.delete(key);
      throw error;
    });

  pendingRequests.set(key, request);
  return request;
}

// --- 🧹 Cache invalidation helpers ---
export function invalidateCacheByUrl(url: string) {
  // Keys are like: "GET:/admin/clients?{}:{}"
  const prefix = `GET:${url}?`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

/**
 * More flexible invalidation (use when you need to wipe multiple endpoints).
 * Example: invalidateCache(key => key.startsWith('GET:/admin/clients'));
 */
export function invalidateCache(predicate: (key: string) => boolean) {
  for (const key of Array.from(cache.keys())) {
    if (predicate(key)) cache.delete(key);
  }
}

// const getCookie = (name: string): string | null => {
//   const value = `; ${document.cookie}`;
//   const parts = value.split(`; ${name}=`);
//   if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
//   return null;
// };


api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export default api;